import OrderAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/process/order/order.entity';
import { territoryIdOf } from 'app/modules/components/routing/sub-routes/route-optimization/delivery-territory';
import { hasPlanIdentity, isValidGeoPoint } from 'app/modules/components/routing/sub-routes/route-optimization/route-optimization.mapper';

/**
 * Partición de las órdenes de una oficina en **grupos de planificación**.
 *
 * Por qué existe: el costo del motor de ruteo es superlineal en el tamaño del
 * problema (la matriz es O(N²) y la búsqueda peor que lineal), así que un
 * único problema con las 600–700 órdenes de una oficina es órdenes de magnitud
 * más caro que N problemas chicos. La partición no es una heurística inventada:
 * usa la agrupación que el negocio **ya declaró** en cada orden (`subRoute`),
 * que además es la que operaciones ejecuta — un camión no cruza sub-rutas.
 *
 * Módulo puro: entra un arreglo de órdenes, salen grupos y exclusiones. Sin HTTP.
 */

/**
 * Motivo por el que una orden no llega al motor.
 *
 * - `invalid_geo`: sin `geolocationPoint` usable — no se puede rutear.
 * - `missing_identity`: sin `_id` o sin `shipTo` — el contrato de `/plan` exige
 *   ambos no vacíos y responde `422`, tumbando el grupo entero por una orden.
 */
export type ExcludedReason = 'invalid_geo' | 'missing_identity';

/** Una orden que no se puede planificar, con el motivo (nunca se descarta en silencio). */
export interface ExcludedOrder {
  order: OrderAttributes;
  reason: ExcludedReason;
}

/**
 * De dónde salió la clave del grupo. Determina si se le puede resolver una
 * flota desde el maestro (`sub-route`) o si va con la de respaldo.
 */
export type GroupOrigin = 'sub-route' | 'municipality' | 'office';

/** Un subproblema de planificación: las órdenes que se resuelven juntas. */
export interface PlanningGroup {
  /** Identificador único del grupo dentro de la corrida. */
  key: string;
  /** Cómo se formó el grupo. */
  origin: GroupOrigin;
  /** Etiqueta legible (nombre de sub-ruta o de municipio). */
  label: string;
  /** Sub-ruta del grupo, cuando la tiene: la llave para resolver su flota. */
  subRouteId?: string;
  orders: OrderAttributes[];
}

/** Cuántas órdenes llegan al motor y cuántas no, con el porqué. */
export interface PlanningCoverage {
  /** Órdenes cargadas de la oficina. */
  loaded: number;
  /** Las que se pueden planificar (tienen coordenadas válidas). */
  plannable: number;
  /** Excluidas por `geolocationPoint` ausente, incompleto o en Null Island. */
  invalidGeo: number;
  /** Excluidas por no traer `_id` o `shipTo` (el contrato exige ambos). */
  missingIdentity: number;
  /** Planificables que no traen `subRoute` y se agrupan por municipio o residual. */
  withoutSubRoute: number;
  /** Huérfanas de ruta ASIGNADAS a una (por territorio o cercanía). */
  autoAssigned: number;
  /**
   * Planificables que SÍ llegan al motor. Solo lo llena `partitionByTerritory`:
   * con la partición por territorio, ser planificable ya no alcanza para
   * planificarse — también hace falta que su territorio tenga camión.
   */
  planned?: number;
  /**
   * Planificables sin flota que los pueda llevar. `planned + unplanned` iguala
   * `plannable`: es la reconciliación que la pantalla muestra para que el conteo
   * no mienta sobre lo que realmente se envió.
   */
  unplanned?: number;
}

/** Una orden huérfana asignada automáticamente a una ruta, con su porqué. */
export interface AutoAssignment {
  order: OrderAttributes;
  subRouteId: string;
  subRouteName: string;
  /**
   * Cómo se resolvió: `territory` = una sola ruta declara su territorio;
   * `territory-nearest` = varias lo declaran y ganó la más cercana;
   * `nearest` = sin territorio que mapee, ganó cercanía pura (con umbral).
   */
  method: 'territory' | 'territory-nearest' | 'nearest';
  /** Distancia (km, línea recta) a la parada más cercana de la ruta elegida. */
  distanceKm?: number;
}

export interface PartitionResult {
  groups: PlanningGroup[];
  excluded: ExcludedOrder[];
  coverage: PlanningCoverage;
  /** Huérfanas de ruta resueltas automáticamente (territorio o cercanía). */
  autoAssigned: AutoAssignment[];
  /**
   * Clave de grupo REAL de cada orden auto-asignada. `planningGroupKeyOf` deriva
   * la clave de la ORDEN (que no tiene ruta), así que sin este mapa una
   * auto-asignada que quede sin ubicar caería bajo el tag equivocado.
   */
  groupKeyByOrderId: Map<string, string>;
}

/**
 * Lo mínimo del maestro de rutas para poder resolver huérfanas.
 *
 * `routes[].id` es `unknown` porque el backend lo manda como número Y como texto
 * (ver `territoryIdOf`); tipearlo `number` hacía que el cruce mal tipado pasara
 * el compilador sin ruido.
 */
export interface MasterSubRouteLike {
  _id?: unknown;
  name?: string;
  routes?: Array<{ id?: unknown; name?: unknown }>;
  function?: { name?: string };
}

export interface PartitionOptions {
  /**
   * Techo de paradas por grupo. Un grupo que lo supera se subdivide por
   * municipio (ver `partitionOrders`). Es una salvaguarda contra una sub-ruta
   * desproporcionada, no el camino normal.
   */
  maxGroupSize?: number;
  /**
   * Etiqueta del grupo residual (sin ruta ni municipio). Cada pantalla pasa la
   * suya; sin ella se usa `DEFAULT_RESIDUAL_LABEL`.
   */
  residualLabel?: string;
  /**
   * Maestro de rutas de LA MISMA oficina, para resolver huérfanas: siempre que
   * el pedido tenga un territorio que alguna ruta declare, se asigna a ella
   * (si varias lo declaran, gana la más cercana); sin territorio que mapee,
   * gana cercanía pura con umbral. Ausente = comportamiento anterior
   * (municipio/residual). El llamador acota a la oficina: cruzar oficinas
   * asignaría pedidos a camiones que salen de otro almacén.
   */
  masterSubRoutes?: readonly MasterSubRouteLike[];
}

/**
 * Umbral de la asignación por cercanía PURA (sin territorio que avale): más
 * lejos que esto de toda parada, el pedido queda por municipio. Sin umbral, un
 * pedido a 40 km se subiría al camión "menos lejano" en silencio y se notaría
 * cuando el camión no vuelve a tiempo.
 */
export const NEARBY_ASSIGN_MAX_KM = 5;

/** Techo por defecto: holgado frente a las ~33 paradas típicas por sub-ruta. */
export const DEFAULT_MAX_GROUP_SIZE = 120;

/**
 * Clave del grupo residual de la oficina (sin sub-ruta ni municipio).
 *
 * Exportada porque la pantalla la necesita como respaldo al derivar la entrega
 * de un punto cuya orden no pudo resolver: dos copias del literal divergen sin
 * dar error, solo listas vacías.
 */
export const RESIDUAL_KEY = 'office:residual';
/**
 * Etiqueta por defecto del grupo residual.
 *
 * Llega a ser el **título de una tarjeta**, así que su vocabulario depende de la
 * pantalla: "Rutas AI" habla de sub-rutas y Asignación de Carga no. Se conserva
 * este valor como default —para no cambiar "Rutas AI"— y se puede sobrescribir
 * con `PartitionOptions.residualLabel`.
 */
export const DEFAULT_RESIDUAL_LABEL = 'Sin sub-ruta ni municipio';

/** Coordenada de entrega de la orden (`address.geolocationPoint = [lat, lon]`). */
function geoOf(order: OrderAttributes): number[] | undefined {
  return order?.address?.geolocationPoint;
}

/** Normaliza un id de Mongo (string u ObjectId) a string, o `undefined`. */
function idOf(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  const id = String(value);
  return id.length ? id : undefined;
}

type Ref = { _id?: unknown; name?: string } | undefined;

function subRouteRef(order: OrderAttributes): Ref {
  return (order as unknown as { subRoute?: Ref }).subRoute;
}

function municipalityRef(order: OrderAttributes): Ref {
  return (order as unknown as { address?: { locationThree?: Ref } }).address?.locationThree;
}

/**
 * Territorios que declara la orden, con el id normalizado.
 *
 * Se normaliza porque el mismo territorio llega como `62` en un pedido y `'62'`
 * en otro (medido sobre datos reales, con arreglos de tipos mezclados). Un
 * `Set<number>` sin normalizar da `has('62') === false` y el cruce falla sin
 * lanzar nada: el síntoma es un pedido que se cae del territorio, no un error.
 */
function territoryIdsOf(order: OrderAttributes): Set<number> {
  const routes = (order as unknown as { routes?: Array<{ id?: unknown }> }).routes ?? [];
  return new Set(
    routes.map((r) => territoryIdOf(r?.id)).filter((id): id is number => id !== undefined),
  );
}

/** Territorios que declara una sub-ruta del maestro, con el id normalizado. */
function masterTerritoriesOf(master: MasterSubRouteLike): Map<number, string> {
  const out = new Map<number, string>();
  for (const route of master.routes ?? []) {
    const id = territoryIdOf(route?.id);
    if (id === undefined || out.has(id)) continue;
    out.set(id, route?.name === undefined || route?.name === null ? '' : String(route.name));
  }
  return out;
}

/**
 * ¿La ruta es elegible para recibir huérfanas? Solo las de ENTREGA: un
 * territorio también puede declararlo una ruta de PREVENTA, y asignarle un
 * pedido de entrega a la ruta de un vendedor sería un error silencioso. Una
 * ruta SIN función declarada se admite: el campo es opcional en el maestro y
 * excluirla apagaría la resolución justo donde el dato falta.
 *
 * Exportado porque `planning-catalogs` deriva de él los catálogos de territorios
 * y camiones de la corrida, y es LA MISMA regla: dos copias divergirían sin dar
 * error. El parámetro pide solo `function` a propósito, para que lo pueda
 * satisfacer tanto `MasterSubRouteLike` como un `SubRouteAttributes` completo.
 */
export function isDeliverySubRoute(master: { function?: { name?: string } }): boolean {
  const name = master.function?.name;
  return name == null || name.trim().toUpperCase() === 'ENTREGA';
}

/**
 * Distancia en línea recta (km). Suficiente a escala de ciudad para desempatar.
 *
 * Exportada porque el gestor de confirmación mide con ella el desvío de cada ruta
 * al incorporar un pedido que no entró al plan (ver `pending-orders`). Es LA MISMA
 * pregunta que resuelve la escalera de cercanía de la partición, y dos copias
 * divergirían sin dar error.
 */
export function haversineKm(a: [number, number], b: [number, number]): number {
  const rad = Math.PI / 180;
  const dLat = (b[0] - a[0]) * rad;
  const dLon = (b[1] - a[1]) * rad;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a[0] * rad) * Math.cos(b[0] * rad) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

/**
 * Distancia a la parada más cercana de una ruta, o `undefined` si no tiene.
 *
 * Exportada por lo mismo que `haversineKm`: es la medida del desvío que el gestor
 * muestra al incorporar un pedido pendiente.
 */
export function nearestStopKm(point: [number, number], stops: readonly [number, number][]): number | undefined {
  let best: number | undefined;
  for (const stop of stops) {
    const d = haversineKm(point, stop);
    if (best === undefined || d < best) best = d;
  }
  return best;
}

/**
 * Resuelve la ruta de una huérfana contra el maestro y la geometría de la
 * corrida. La escalera, en orden de certeza:
 * 1. UNA ruta declara su territorio → esa (el maestro manda).
 * 2. VARIAS lo declaran → la más cercana entre ellas (empate geográfico dentro
 *    de lo que el maestro avala). Sin geometría para medir, no se adivina.
 * 3. NINGUNA → cercanía pura, con umbral `NEARBY_ASSIGN_MAX_KM`.
 */
function resolveOrphan(
  order: OrderAttributes,
  masters: readonly MasterSubRouteLike[],
  stopsBySubRouteId: ReadonlyMap<string, [number, number][]>,
): { master: MasterSubRouteLike; method: AutoAssignment['method']; distanceKm?: number } | undefined {
  const geo = geoOf(order) as [number, number];
  const territories = territoryIdsOf(order);
  const eligible = masters.filter(isDeliverySubRoute);

  const byTerritory = eligible.filter((m) =>
    [...masterTerritoriesOf(m).keys()].some((id) => territories.has(id)),
  );

  if (byTerritory.length === 1) {
    const master = byTerritory[0];
    const stops = stopsBySubRouteId.get(String(master._id)) ?? [];
    return { master, method: 'territory', distanceKm: nearestStopKm(geo, stops) };
  }

  const pickNearest = (
    candidates: readonly MasterSubRouteLike[],
  ): { master: MasterSubRouteLike; distanceKm: number } | undefined => {
    let best: { master: MasterSubRouteLike; distanceKm: number } | undefined;
    for (const master of candidates) {
      const stops = stopsBySubRouteId.get(String(master._id));
      if (!stops?.length) continue; // sin paradas hoy no hay geometría que medir
      const d = nearestStopKm(geo, stops);
      if (d !== undefined && (best === undefined || d < best.distanceKm)) {
        best = { master, distanceKm: d };
      }
    }
    return best;
  };

  if (byTerritory.length > 1) {
    const nearest = pickNearest(byTerritory);
    return nearest ? { ...nearest, method: 'territory-nearest' } : undefined;
  }

  const nearest = pickNearest(eligible);
  if (nearest && nearest.distanceKm <= NEARBY_ASSIGN_MAX_KM) {
    return { ...nearest, method: 'nearest' };
  }
  return undefined;
}

/**
 * Llave del grupo de planificación al que pertenece una orden.
 *
 * **Única definición del formato.** `partitionOrders` la usa para agrupar y la
 * pantalla para saber a qué entrega pertenece un punto que quedó fuera de las
 * rutas —un no asignado, un excluido—. Con dos copias del formato, el día que
 * cambie el prefijo el filtro dejaría de emparejar en silencio: no habría error,
 * solo listas vacías.
 *
 * Ojo con la salvaguarda de tamaño: un grupo que se subdivide por municipio
 * cambia su `key`, no lo que esta función devuelve. Eso es deliberado — la
 * identidad de la entrega es la sub-ruta, no el pedazo en que se partió.
 */
export function planningGroupKeyOf(order: OrderAttributes): string {
  const subRouteId = idOf(subRouteRef(order)?._id);
  if (subRouteId) return `sub-route:${subRouteId}`;
  const municipalityId = idOf(municipalityRef(order)?._id);
  if (municipalityId) return `municipality:${municipalityId}`;
  return RESIDUAL_KEY;
}

/**
 * Cobertura de los datos que la planificación necesita.
 *
 * `plannable + invalidGeo + missingIdentity === loaded` siempre: es la
 * reconciliación que la pantalla muestra para que el conteo de órdenes cargadas
 * no mienta sobre lo que realmente se envía al motor. `withoutSubRoute` solo
 * cuenta planificables — una orden ya excluida nunca se va a agrupar.
 */
export function planningCoverage(orders: OrderAttributes[]): PlanningCoverage {
  const list = orders ?? [];
  let invalidGeo = 0;
  let missingIdentity = 0;
  const plannable: OrderAttributes[] = [];
  for (const order of list) {
    if (!isValidGeoPoint(geoOf(order))) invalidGeo++;
    else if (!hasPlanIdentity(order)) missingIdentity++;
    else plannable.push(order);
  }
  return {
    loaded: list.length,
    plannable: plannable.length,
    invalidGeo,
    missingIdentity,
    withoutSubRoute: plannable.filter((o) => !idOf(subRouteRef(o)?._id)).length,
    autoAssigned: 0,
  };
}

/**
 * Reparte las órdenes en grupos de planificación.
 *
 * Cascada de agrupación, en este orden:
 * 1. `subRoute._id` — la unidad operativa real (declara vehículo, itinerario,
 *    ventanas y multi-viaje en su maestro).
 * 2. `address.locationThree._id` (municipio) — respaldo para las órdenes sin
 *    sub-ruta. Es geográfico de verdad, así que produce grupos razonables sin
 *    inventar clustering.
 * 3. Un único grupo residual de la oficina, para las que no tienen ninguno.
 *
 * Las órdenes que no pueden planificarse —sin coordenadas válidas, o sin la
 * identidad que el contrato exige— se apartan **antes** de agrupar y se
 * devuelven en `excluded`: si se filtraran en silencio (como hacía el mapper),
 * la pantalla mostraría un conteo que no coincide con lo que se envía, y una
 * sola orden mal formada haría fallar la petición de todo su grupo.
 *
 * Salvaguarda de tamaño: un grupo con más de `maxGroupSize` paradas se subdivide
 * por municipio conservando su sub-ruta, para no reintroducir el problema
 * cuadrático dentro del grupo. Si todas las paradas caen en el mismo municipio
 * no hay por dónde partir y el grupo queda grande — es el límite conocido de
 * esta salvaguarda.
 *
 * El orden de entrada se preserva dentro de cada grupo, y los grupos salen en
 * el orden en que aparecieron.
 */
export function partitionOrders(
  orders: OrderAttributes[],
  options: PartitionOptions = {},
): PartitionResult {
  const maxGroupSize = options.maxGroupSize ?? DEFAULT_MAX_GROUP_SIZE;
  const list = orders ?? [];

  const excluded: ExcludedOrder[] = [];
  const plannable: OrderAttributes[] = [];
  for (const order of list) {
    if (!isValidGeoPoint(geoOf(order))) excluded.push({ order, reason: 'invalid_geo' });
    else if (!hasPlanIdentity(order)) excluded.push({ order, reason: 'missing_identity' });
    else plannable.push(order);
  }

  const byKey = new Map<string, PlanningGroup>();
  const push = (group: Omit<PlanningGroup, 'orders'>, order: OrderAttributes): void => {
    const existing = byKey.get(group.key);
    if (existing) existing.orders.push(order);
    else byKey.set(group.key, { ...group, orders: [order] });
  };

  // Primera pasada: las que traen ruta. Además de agruparlas, fijan la
  // GEOMETRÍA de la corrida (las paradas de cada ruta hoy), que es contra lo
  // que se mide "cerca" en la segunda pasada. Deliberadamente no se realimenta:
  // una huérfana ya asignada no atrae a la siguiente, para que el resultado no
  // dependa del orden de llegada.
  const orphans: OrderAttributes[] = [];
  const stopsBySubRouteId = new Map<string, [number, number][]>();
  for (const order of plannable) {
    const subRoute = subRouteRef(order);
    const subRouteId = idOf(subRoute?._id);
    if (!subRouteId) {
      orphans.push(order);
      continue;
    }
    push(
      { key: planningGroupKeyOf(order), origin: 'sub-route', label: subRoute?.name ?? subRouteId, subRouteId },
      order,
    );
    const stops = stopsBySubRouteId.get(subRouteId) ?? [];
    stops.push(geoOf(order) as [number, number]);
    stopsBySubRouteId.set(subRouteId, stops);
  }

  // Segunda pasada: huérfanas. Con maestro se intenta la escalera
  // territorio → territorio+cercanía → cercanía con umbral; lo irresoluble cae
  // a municipio/residual, como siempre.
  const autoAssigned: AutoAssignment[] = [];
  const groupKeyByOrderId = new Map<string, string>();
  const masters = options?.masterSubRoutes ?? [];
  for (const order of orphans) {
    const resolved = masters.length
      ? resolveOrphan(order, masters, stopsBySubRouteId)
      : undefined;

    if (resolved && resolved.master._id != null) {
      const subRouteId = String(resolved.master._id);
      const key = `sub-route:${subRouteId}`;
      push(
        { key, origin: 'sub-route', label: resolved.master.name ?? subRouteId, subRouteId },
        order,
      );
      autoAssigned.push({
        order,
        subRouteId,
        subRouteName: resolved.master.name ?? subRouteId,
        method: resolved.method,
        distanceKm:
          resolved.distanceKm === undefined
            ? undefined
            : Math.round(resolved.distanceKm * 10) / 10,
      });
      const orderId = idOf((order as unknown as { _id?: unknown })._id);
      if (orderId) groupKeyByOrderId.set(orderId, key);
      continue;
    }

    const municipality = municipalityRef(order);
    const municipalityId = idOf(municipality?._id);
    push(
      municipalityId
        ? { key: `municipality:${municipalityId}`, origin: 'municipality', label: municipality?.name ?? municipalityId }
        : { key: RESIDUAL_KEY, origin: 'office', label: options?.residualLabel ?? DEFAULT_RESIDUAL_LABEL },
      order,
    );
  }

  const groups = [...byKey.values()].flatMap((group) =>
    group.orders.length > maxGroupSize ? splitByMunicipality(group) : [group],
  );

  const coverage = planningCoverage(list);
  // La cobertura cuenta lo que QUEDÓ sin ruta, no lo que llegó sin ella: las
  // resueltas ya viajan en una.
  coverage.autoAssigned = autoAssigned.length;
  coverage.withoutSubRoute -= autoAssigned.length;

  return { groups, excluded, coverage, autoAssigned, groupKeyByOrderId };
}

/* ================================================================== *
 *  Partición por TERRITORIO — la que usa la flota real                *
 * ================================================================== */

/**
 * Por qué un pedido planificable NO llega al motor.
 *
 * Es distinto de `ExcludedReason`: ahí el pedido no se puede planificar por su
 * propio dato (sin coordenadas, sin identidad); acá el pedido está bien y lo que
 * falta es **con qué llevarlo**. Separarlos importa porque se arreglan en lugares
 * distintos: uno en el pedido, el otro asignando un camión en el maestro.
 */
export type UnplannedReason =
  /**
   * Su territorio TIENE ruta de entrega, pero esa ruta no declara vehículo.
   *
   * Se arregla asignando el vehículo en el maestro de rutas. Antes esta causa se
   * repartía en dos motivos según por cuál pasada entró el pedido —un detalle del
   * algoritmo que al operador no le dice nada— y compartía etiqueta con dos causas
   * que se arreglan en otro lado.
   */
  | 'route_without_vehicle'
  /**
   * Ninguna ruta de entrega declara su territorio.
   *
   * Se arregla asignándole una ruta al territorio, no un vehículo: no hay ruta a la
   * que asignárselo.
   */
  | 'territory_without_route'
  /** El pedido no trae ningún territorio: se carga en la dirección del cliente. */
  | 'client_without_territory'
  /**
   * El camión de su territorio estaba disputado y se asignó a OTRO territorio.
   *
   * Distinto de `route_without_vehicle`: ahí falta asignar un camión en el maestro;
   * acá el camión existe y está en otra parte. Se resuelven distinto, así que decir
   * lo mismo en los dos casos manda al operador al lugar equivocado.
   */
  | 'vehicle_taken'
  /** Su sub-ruta es de PREVENTA y ningún territorio suyo tiene flota. */
  | 'preventa_subroute'
  ;

/** Un pedido que no se planifica, con el motivo y el territorio si se supo. */
export interface UnplannedOrder {
  order: OrderAttributes;
  reason: UnplannedReason;
  territoryId?: number;
  territoryName?: string;
  /** Territorio al que se fue el camión, cuando el motivo es `vehicle_taken`. */
  takenBy?: string;
}

/** Un subproblema: los pedidos de UN territorio, que su flota resuelve junta. */
export interface TerritoryGroup {
  /** `territory:<id>` — única definición del formato (ver `territoryGroupKey`). */
  key: string;
  territoryId: number;
  territoryName: string;
  orders: OrderAttributes[];
}

/**
 * Cómo quedó resuelto el territorio de un pedido agrupado. **Se emite para todos.**
 *
 * `method: 'sub-route'` —su ruta de entrega lo declara— es el caso mayoritario y
 * también viaja: es el tramo que explica la mayoría de las paradas. Un consumidor
 * que solo quiera las que resolvió la escalera lo descarta por `method`.
 */
export interface TerritoryAssignment {
  order: OrderAttributes;
  territoryId: number;
  territoryName: string;
  /**
   * `sub-route` = su ruta de ENTREGA lo declara (el maestro manda);
   * `territory` = un solo territorio suyo tiene flota;
   * `territory-nearest` = varios la tienen y ganó el más cercano.
   */
  method: 'sub-route' | 'territory' | 'territory-nearest';
  distanceKm?: number;
}

export interface TerritoryPartitionResult {
  groups: TerritoryGroup[];
  /** No planificables por su propio dato (geo/identidad). */
  excluded: ExcludedOrder[];
  /** Planificables sin flota que los pueda llevar, con motivo. */
  unplanned: UnplannedOrder[];
  coverage: PlanningCoverage;
  assignments: TerritoryAssignment[];
  /**
   * Territorio (clave de grupo) de cada pedido planificado, por `_id`.
   *
   * Es la ÚNICA fuente: con partición por territorio la clave ya no se puede
   * derivar del pedido solo —depende de qué territorios tienen flota—, así que
   * `plan-result` la consulta acá en vez de recalcularla.
   */
  groupKeyByOrderId: Map<string, string>;
}

export interface TerritoryPartitionOptions {
  /**
   * Territorios CON flota asignada: los únicos que se planifican. Un territorio
   * que no está acá manda sus pedidos a `unplanned`, nunca a un grupo.
   */
  fleetTerritoryIds: readonly number[];
  /**
   * Maestro de sub-rutas de LA MISMA oficina, indexable por `_id`. De acá sale el
   * territorio que declara la ruta de un pedido y si esa ruta es de ENTREGA.
   */
  masterSubRoutes?: readonly MasterSubRouteLike[];
  /**
   * Territorios que PERDIERON su camión porque estaba disputado, con el nombre del
   * territorio al que se fue. Sus pedidos salen con `vehicle_taken` en vez de una
   * falta de flota: el camión existe, está en otra parte, y eso se arregla distinto
   * que asignando uno nuevo en el maestro.
   */
  takenTerritories?: ReadonlyMap<number, string>;
  /**
   * Territorios que alguna ruta de ENTREGA declara, **con o sin vehículo**.
   *
   * Es lo que separa "su ruta no tiene vehículo" de "no tiene ruta": dos causas con
   * arreglos distintos que sin este conjunto caían bajo el mismo motivo. Ausente =
   * no se distinguen, y todo territorio sin flota se reporta como sin ruta.
   */
  territoriesWithRoute?: readonly number[];
}

/** Clave del grupo de un territorio. **Única definición del formato.** */
export function territoryGroupKey(territoryId: number): string {
  return `territory:${territoryId}`;
}

/**
 * Reparte las órdenes por **territorio con flota**.
 *
 * Reemplaza a `partitionOrders`, que agrupaba por `order.subRoute._id`. El
 * problema de esa agrupación no era el criterio sino su consecuencia: producía
 * tantos grupos como sub-rutas aparecieran en los datos —incluidas las de
 * PREVENTA y los grupos por municipio— y cada grupo pedía un camión, así que una
 * oficina con 2 camiones asignados generaba 21 rutas con 19 camiones inventados.
 *
 * Acá la flota es el punto de partida: solo los territorios que tienen camión
 * son grupos, y lo que no entra se declara. La regla "un camión entrega en un
 * territorio" queda garantizada por construcción — un camión nunca comparte
 * problema con los pedidos de otro territorio.
 *
 * Escalera para resolver el territorio de un pedido (ver `design.md`, Decisión 3):
 *
 * 1. El territorio que declara su sub-ruta de **ENTREGA**. El maestro manda:
 *    `order.routes` está contaminado (hay pedidos con 28 territorios que abarcan
 *    Tegucigalpa y Comayagua a la vez) mientras la sub-ruta es una afirmación.
 * 2. `order.routes` ∩ territorios **de la flota**. Acotar a la flota y no al
 *    catálogo completo es lo que hace útil la intersección: responde "¿hay un
 *    camión que pueda llevarlo?" en vez de "¿a qué zonas pertenece?".
 * 3. Con varios candidatos, el más cercano por geometría del día.
 *
 * NO hay salvaguarda de tamaño. `partitionOrders` partía un grupo grande por
 * municipio; acá partir un territorio partiría también su flota y volvería a
 * romper la invariante `rutas ≤ camiones`. Un territorio grande es un problema
 * grande con sus camiones — el techo lo pone la flota, no el conteo de paradas.
 */
export function partitionByTerritory(
  orders: OrderAttributes[],
  options: TerritoryPartitionOptions,
): TerritoryPartitionResult {
  const list = orders ?? [];
  const fleetIds = new Set(options?.fleetTerritoryIds ?? []);
  const taken = options?.takenTerritories ?? new Map<number, string>();
  // Sin este conjunto no se pueden separar dos causas: "su ruta no tiene vehículo"
  // y "no tiene ruta". Ausente = no se distinguen y todo sale como sin ruta.
  const withRoute = new Set(options?.territoriesWithRoute ?? []);
  const missingFleetReason = (territoryId: number): UnplannedReason =>
    withRoute.has(territoryId) ? 'route_without_vehicle' : 'territory_without_route';
  const masters = new Map<string, MasterSubRouteLike>();
  for (const master of options?.masterSubRoutes ?? []) {
    const id = idOf(master?._id);
    if (id) masters.set(id, master);
  }

  const excluded: ExcludedOrder[] = [];
  const plannable: OrderAttributes[] = [];
  for (const order of list) {
    if (!isValidGeoPoint(geoOf(order))) excluded.push({ order, reason: 'invalid_geo' });
    else if (!hasPlanIdentity(order)) excluded.push({ order, reason: 'missing_identity' });
    else plannable.push(order);
  }

  // Nombre de cada territorio de la flota, para poder rotular el grupo y el
  // motivo sin que el llamador tenga que cruzar catálogos.
  const nameOfTerritory = new Map<number, string>();
  for (const master of masters.values()) {
    for (const [id, name] of masterTerritoriesOf(master)) {
      if (name && !nameOfTerritory.get(id)) nameOfTerritory.set(id, name);
    }
  }

  const groups = new Map<number, TerritoryGroup>();
  const unplanned: UnplannedOrder[] = [];
  const assignments: TerritoryAssignment[] = [];
  const groupKeyByOrderId = new Map<string, string>();
  const stopsByTerritory = new Map<number, [number, number][]>();

  const place = (
    order: OrderAttributes,
    territoryId: number,
    method: TerritoryAssignment['method'],
    distanceKm?: number,
  ): void => {
    const territoryName = nameOfTerritory.get(territoryId) ?? String(territoryId);
    const group =
      groups.get(territoryId) ??
      { key: territoryGroupKey(territoryId), territoryId, territoryName, orders: [] };
    group.orders.push(order);
    groups.set(territoryId, group);

    const stops = stopsByTerritory.get(territoryId) ?? [];
    stops.push(geoOf(order) as [number, number]);
    stopsByTerritory.set(territoryId, stops);

    const orderId = idOf((order as unknown as { _id?: unknown })._id);
    if (orderId) groupKeyByOrderId.set(orderId, group.key);
    // TODOS los agrupados, incluido `sub-route`.
    //
    // Antes se descartaba el caso trivial —"el territorio que declara la propia ruta
    // no es una asignación que haya que justificar: es el dato"—, y eso es cierto
    // para la pregunta que este arreglo respondía: QUÉ HUBO QUE RESOLVER. La otra
    // pregunta, por qué una parada quedó en su camión, necesita nombrar también el
    // caso normal: sin él la mayoría de las paradas no tendría tramo de territorio.
    // El `method` ya se calculaba para todas; lo que se descartaba era la fila.
    //
    // El consumidor que solo quiere las de la escalera filtra por `method`.
    assignments.push({
      order,
      territoryId,
      territoryName,
      method,
      distanceKm: distanceKm === undefined ? undefined : Math.round(distanceKm * 10) / 10,
    });
  };

  /**
   * Primera pasada: los pedidos cuya ruta de ENTREGA declara UN territorio. Son
   * los que fijan la geometría del día contra la que la segunda pasada mide
   * "cerca". Deliberadamente no se realimenta: un pedido resuelto por cercanía no
   * atrae al siguiente, para que el resultado no dependa del orden de llegada.
   */
  const deferred: OrderAttributes[] = [];
  for (const order of plannable) {
    const subRouteId = idOf(subRouteRef(order)?._id);
    const master = subRouteId ? masters.get(subRouteId) : undefined;
    if (!master || !isDeliverySubRoute(master)) {
      deferred.push(order);
      continue;
    }
    const declared = masterTerritoriesOf(master);
    if (declared.size !== 1) {
      // Una ruta de ENTREGA declara exactamente un territorio en los datos
      // medidos (39 de 39). Si declara varios, no se elige por posición: cae a la
      // intersección con la flota, que sí es una respuesta justificable.
      deferred.push(order);
      continue;
    }
    const [territoryId, territoryName] = [...declared.entries()][0];
    if (territoryName && !nameOfTerritory.has(territoryId)) {
      nameOfTerritory.set(territoryId, territoryName);
    }
    if (!fleetIds.has(territoryId)) {
      const takenBy = taken.get(territoryId);
      unplanned.push({
        order,
        reason: takenBy ? 'vehicle_taken' : missingFleetReason(territoryId),
        territoryId,
        territoryName: nameOfTerritory.get(territoryId) ?? String(territoryId),
        takenBy,
      });
      continue;
    }
    place(order, territoryId, 'sub-route');
  }

  // Segunda pasada: intersección con los territorios de la flota.
  for (const order of deferred) {
    const subRouteId = idOf(subRouteRef(order)?._id);
    const master = subRouteId ? masters.get(subRouteId) : undefined;
    const isPreventa = !!master && !isDeliverySubRoute(master);

    const candidates = [...territoryIdsOf(order)].filter((id) => fleetIds.has(id));
    if (candidates.length === 1) {
      place(order, candidates[0], 'territory');
      continue;
    }
    if (candidates.length > 1) {
      const geo = geoOf(order) as [number, number];
      let best: { territoryId: number; distanceKm: number } | undefined;
      for (const territoryId of candidates) {
        const stops = stopsByTerritory.get(territoryId);
        if (!stops?.length) continue; // sin geometría hoy no hay nada que medir
        const d = nearestStopKm(geo, stops);
        if (d !== undefined && (best === undefined || d < best.distanceKm)) {
          best = { territoryId, distanceKm: d };
        }
      }
      if (best) {
        place(order, best.territoryId, 'territory-nearest', best.distanceKm);
        continue;
      }
      // Varios candidatos y ninguno con paradas: se toma el de menor id para que
      // el resultado sea determinista en vez de depender del orden del Set.
      place(order, [...candidates].sort((a, b) => a - b)[0], 'territory');
      continue;
    }

    // Su territorio no tiene flota, pero puede ser que la haya PERDIDO: el camión
    // existe y se fue a otro territorio. Es el mismo motivo que en la primera
    // pasada, y decir "sin territorio que mapee" acá mandaría a revisar el pedido
    // cuando el problema es de flota.
    const takenId = [...territoryIdsOf(order)].find((id) => taken.has(id));
    if (takenId !== undefined) {
      unplanned.push({
        order,
        reason: 'vehicle_taken',
        territoryId: takenId,
        territoryName: nameOfTerritory.get(takenId) ?? String(takenId),
        takenBy: taken.get(takenId),
      });
      continue;
    }

    // La misma causa que en la primera pasada tiene que dar el MISMO motivo: el
    // operador no distingue si el pedido traía su sub-ruta, y antes eso decidía la
    // etiqueta. Se mira el territorio del pedido, no cómo se llegó a él.
    const own = [...territoryIdsOf(order)];
    const withRouteOwn = own.find((id) => withRoute.has(id));
    if (withRouteOwn !== undefined) {
      unplanned.push({
        order,
        reason: 'route_without_vehicle',
        territoryId: withRouteOwn,
        territoryName: nameOfTerritory.get(withRouteOwn) ?? String(withRouteOwn),
      });
      continue;
    }
    if (isPreventa) {
      unplanned.push({ order, reason: 'preventa_subroute' });
      continue;
    }
    unplanned.push({
      order,
      // Sin ningún territorio el problema es del CLIENTE, no de la flota: se carga
      // en su dirección. Con territorio pero sin ruta, se le asigna una ruta.
      reason: own.length ? 'territory_without_route' : 'client_without_territory',
      ...(own.length
        ? {
            territoryId: own[0],
            territoryName: nameOfTerritory.get(own[0]) ?? String(own[0]),
          }
        : {}),
    });
  }

  // Los EXCLUIDOS por dato también reciben la clave de su territorio cuando se
  // puede saber: el filtro por entrega de la pantalla los muestra junto a las
  // paradas de esa ruta, y sin clave quedaban sin tag —se mostraban todos o se
  // escondían todos—. No llegan al grupo, pero pertenecen a la entrega.
  for (const { order } of excluded) {
    const orderId = idOf((order as unknown as { _id?: unknown })._id);
    if (!orderId || groupKeyByOrderId.has(orderId)) continue;
    const subRouteId = idOf(subRouteRef(order)?._id);
    const master = subRouteId ? masters.get(subRouteId) : undefined;
    const declared = master && isDeliverySubRoute(master) ? masterTerritoriesOf(master) : undefined;
    const territoryId =
      declared?.size === 1
        ? [...declared.keys()][0]
        : [...territoryIdsOf(order)].find((id) => fleetIds.has(id));
    if (territoryId !== undefined && groups.has(territoryId)) {
      groupKeyByOrderId.set(orderId, territoryGroupKey(territoryId));
    }
  }

  const coverage = planningCoverage(list);
  // Solo las que resolvió la ESCALERA. `assignments` ahora trae también el caso
  // trivial —el territorio que declara la propia ruta—, y contarlo acá haría que la
  // insignia "asignados a su ruta" reportara la oficina entera como huérfana resuelta.
  coverage.autoAssigned = assignments.filter((a) => a.method !== 'sub-route').length;
  coverage.planned = [...groups.values()].reduce((n, g) => n + g.orders.length, 0);
  coverage.unplanned = unplanned.length;

  return {
    groups: [...groups.values()].sort((a, b) => a.territoryName.localeCompare(b.territoryName)),
    excluded,
    unplanned,
    coverage,
    assignments,
    groupKeyByOrderId,
  };
}

/**
 * Parte un grupo desproporcionado en subgrupos por municipio, conservando su
 * `origin` y su `subRouteId` para que la resolución de flota siga encontrando
 * el mismo camión. Las órdenes sin municipio quedan juntas en un subgrupo.
 */
function splitByMunicipality(group: PlanningGroup): PlanningGroup[] {
  const bySubKey = new Map<string, PlanningGroup>();
  for (const order of group.orders) {
    const municipality = municipalityRef(order);
    const municipalityId = idOf(municipality?._id) ?? 'sin-municipio';
    const key = `${group.key}|municipality:${municipalityId}`;
    const existing = bySubKey.get(key);
    if (existing) {
      existing.orders.push(order);
      continue;
    }
    bySubKey.set(key, {
      ...group,
      key,
      label: municipality?.name ? `${group.label} · ${municipality.name}` : group.label,
      orders: [order],
    });
  }
  return [...bySubKey.values()];
}
